import React, { useState } from "react";
import Header from "../components/Header";
import { orderService } from "../services/apiService";
import Toast from "../components/Toast";
import ReturnModal from "../components/ReturnModal";
import ReturnHistoryModal from "../components/ReturnHistoryModal";

const ReturnSearch = () => {
  const [searchInput, setSearchInput] = useState("");
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchInput.trim()) {
      setToast({ type: "error", message: "Enter Order ID or Customer Mobile" });
      return;
    }
    setLoading(true);
    setOrders([]);
    setSelectedOrder(null);
    try {
      const input = searchInput.trim();
      if (/^\d+$/.test(input) && input.length <= 8) {
        // Direct order ID — fetch detail
        const detail = await orderService.getOrderDetail(input);
        setSelectedOrder(detail);
        setOrders([]);
      } else {
        // Mobile or text — search orders list
        const data = await orderService.searchOrders({
          customerMobile: input.length === 10 ? input : null,
          orderNo: input.length < 10 ? input : null,
          page: 0,
          size: 50,
        });
        const list = data.content ?? [];
        if (list.length === 0) {
          setToast({ type: "error", message: "No orders found" });
        } else if (list.length === 1) {
          const detail = await orderService.getOrderDetail(list[0].orderId);
          setSelectedOrder(detail);
          setOrders([]);
        } else {
          setOrders(list);
        }
      }
    } catch {
      setToast({ type: "error", message: "Failed to fetch order" });
    } finally {
      setLoading(false);
    }
  };

  const selectOrder = async (orderId) => {
    setDetailLoading(true);
    try {
      const detail = await orderService.getOrderDetail(orderId);
      setSelectedOrder(detail);
    } catch {
      setToast({ type: "error", message: "Failed to load order details" });
    } finally {
      setDetailLoading(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      RETURNED: "bg-red-100 text-red-700 border-red-200",
      PARTIAL_RETURN: "bg-orange-100 text-orange-700 border-orange-200",
      PENDING: "bg-blue-100 text-blue-700 border-blue-200",
      CONFIRMED: "bg-green-100 text-green-700 border-green-200",
    };
    return map[status] || "bg-gray-100 text-gray-700 border-gray-200";
  };

  return (
    <div className="h-full flex flex-col overflow-auto bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4">
      <Header showSearch={false} showCart={false} />
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}

      {/* Search */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-4 flex-shrink-0">
        <div className="bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 px-4 py-2.5">
          <h2 className="text-sm font-bold text-blue-800">
            <i className="fas fa-undo-alt mr-2" />
            Return / Refund
          </h2>
        </div>
        <div className="p-2">
          <form onSubmit={handleSearch}>
            <div className="flex flex-wrap gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Order ID or Customer Mobile
                </label>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ""); // allow only digits
                    if (value.length <= 10) {
                      setSearchInput(value);
                    }
                  }}
                  placeholder="e.g. 1234 or 9876543210"
                  className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button
                type="submit"
                disabled={loading}
                className={`flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-full text-white font-semibold shadow text-xs transition ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"
                }`}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search" />
                    Search
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Orders List (when multiple orders found) */}
      {orders.length > 1 && !selectedOrder && (
        <section className="relative mt-2 rounded-xl overflow-hidden shadow-md border border-blue-200 flex flex-col flex-1 min-h-0">
          <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 flex-shrink-0">
            <h3 className="text-base font-bold text-blue-800">
              📋 Order Results
              <span className="text-xs font-normal text-blue-600 ml-2">
                ({orders.length} orders — select one to process return)
              </span>
            </h3>
          </div>
          <div className="overflow-x-auto flex-1">
            {orders.map((o, idx) => (
              <div
                key={o.orderId}
                onClick={() => selectOrder(o.orderId)}
                className={`flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-100 cursor-pointer transition hover:bg-blue-50 ${
                  idx % 2 === 0 ? "bg-white" : "bg-blue-50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <i className="fas fa-receipt text-indigo-500 text-xs" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-800">
                      Order #{o.orderId}{" "}
                      <span className="text-gray-400 font-normal">
                        • {o.billNo || "-"}
                      </span>
                    </p>
                    <p className="text-xs text-gray-400">
                      {o.createdOn
                        ? new Date(o.createdOn).toLocaleString()
                        : "-"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-green-700">
                    ₹
                    {o.finalAmount != null
                      ? Number(o.finalAmount).toFixed(2)
                      : "0"}
                  </span>
                  <span className="text-xs text-gray-500">
                    {o.paymentMode || "-"}
                  </span>
                  <i className="fas fa-chevron-right text-gray-300 text-xs" />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Loading detail */}
      {detailLoading && (
        <div className="flex-1 flex items-center justify-center">
          <i className="fas fa-spinner fa-spin text-blue-400 text-2xl" />
        </div>
      )}

      {/* Order Detail */}
      {selectedOrder && !detailLoading && (
        <section className="relative mt-2 rounded-xl overflow-hidden shadow-md border border-blue-200 flex flex-col flex-1 min-h-0">
          {/* Order Header */}
          <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 flex-shrink-0 flex-wrap gap-2">
            <div>
              <div className="flex items-center gap-2">
                {orders.length > 1 && (
                  <button
                    onClick={() => setSelectedOrder(null)}
                    className="text-blue-600 hover:text-blue-800 transition mr-1"
                    title="Back to list"
                  >
                    <i className="fas fa-arrow-left text-sm" />
                  </button>
                )}
                <h3 className="text-sm font-bold text-blue-800">
                  Order #{selectedOrder.orderId}{" "}
                  <span className="text-xs font-normal text-gray-500 ml-1">
                    • {selectedOrder.billNo || "-"}
                  </span>
                </h3>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {selectedOrder.customerMobile || "-"} •{" "}
                {selectedOrder.createdOn
                  ? new Date(selectedOrder.createdOn).toLocaleString()
                  : "-"}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${statusBadge(selectedOrder.orderStatus)}`}
              >
                {selectedOrder.orderStatus || "ACTIVE"}
              </span>
              <button
                onClick={() => setShowReturnModal(true)}
                disabled={selectedOrder.orderStatus === "RETURNED"}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold text-white shadow transition ${
                  selectedOrder.orderStatus === "RETURNED"
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
                }`}
              >
                <i className="fas fa-undo-alt" /> Initiate Return
              </button>
              {(selectedOrder.orderStatus === "RETURNED" ||
                selectedOrder.orderStatus === "PARTIAL_RETURN") && (
                <button
                  onClick={() => setShowHistory(true)}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition"
                >
                  <i className="fas fa-history" /> View Returns
                </button>
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div className="px-4 py-2 border-b border-gray-100 flex flex-wrap gap-4 sm:gap-6 flex-shrink-0">
            <div>
              <p className="text-xs text-gray-400">Total Amount</p>
              <p className="text-sm font-bold text-blue-700">
                ₹
                {selectedOrder.totalAmount != null
                  ? Number(selectedOrder.totalAmount).toFixed(2)
                  : "0.00"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Final Amount</p>
              <p className="text-sm font-bold text-green-700">
                ₹
                {selectedOrder.finalAmount != null
                  ? Number(selectedOrder.finalAmount).toFixed(2)
                  : "0.00"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Profit</p>
              <p className="text-sm font-bold text-teal-700">
                ₹
                {selectedOrder.totalProfit != null
                  ? Number(selectedOrder.totalProfit).toFixed(2)
                  : "0.00"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Payment</p>
              <p className="text-sm font-semibold text-gray-700">
                {selectedOrder.paymentMode || "-"}
              </p>
            </div>
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto flex-1">
            <table className="min-w-full text-sm text-gray-800 table-auto">
              <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 text-xs uppercase tracking-wide sticky top-0">
                <tr>
                  <th className="py-2 px-3 text-left">#</th>
                  <th className="py-2 px-3 text-left">Product</th>
                  <th className="py-2 px-3 text-center">Qty</th>
                  <th className="py-2 px-3 text-center">Returned</th>
                  <th className="py-2 px-3 text-right">Sell Price</th>
                  <th className="py-2 px-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {(selectedOrder.items || []).map((item, idx) => {
                  const returned = item.returnedQuantity || 0;
                  const fullyReturned = returned >= item.quantity;
                  return (
                    <tr
                      key={idx}
                      className={`border-t hover:bg-blue-50 transition-colors ${fullyReturned ? "bg-red-50" : idx % 2 === 0 ? "bg-white" : "bg-blue-50"}`}
                    >
                      <td className="py-2 px-3 text-gray-400 text-xs">
                        {idx + 1}
                      </td>
                      <td className="py-2 px-3 font-medium text-gray-800 text-xs">
                        {item.productName}
                      </td>
                      <td className="py-2 px-3 text-center text-xs">
                        {item.quantity}
                      </td>
                      <td className="py-2 px-3 text-center text-xs">
                        {returned > 0 ? (
                          <span className="text-red-600 font-semibold">
                            {returned}
                          </span>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right text-xs font-semibold text-green-700">
                        ₹
                        {item.sellingPrice != null
                          ? Number(item.sellingPrice).toFixed(2)
                          : "0.00"}
                      </td>
                      <td className="py-2 px-3 text-center">
                        {fullyReturned ? (
                          <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full border border-red-200">
                            Returned
                          </span>
                        ) : returned > 0 ? (
                          <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full border border-orange-200">
                            Partial
                          </span>
                        ) : (
                          <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full border border-green-200">
                            Active
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Empty state */}
      {!selectedOrder && orders.length === 0 && !loading && !detailLoading && (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-300">
          <i className="fas fa-undo-alt text-5xl mb-3" />
          <p className="text-sm text-gray-400">
            Search an order to process return/refund
          </p>
          <p className="text-xs text-gray-300 mt-1">
            Enter Order ID for direct lookup, or Customer Mobile to see all
            orders
          </p>
        </div>
      )}

      {/* Modals */}
      {showReturnModal && selectedOrder && (
        <ReturnModal
          order={selectedOrder}
          onClose={() => setShowReturnModal(false)}
          onSuccess={(res) => {
            setShowReturnModal(false);
            setToast({
              type: "success",
              message: `Return processed. Refund: \u20B9${res.refundAmount}`,
            });
            selectOrder(selectedOrder.orderId);
          }}
        />
      )}

      {showHistory && selectedOrder && (
        <ReturnHistoryModal
          orderId={selectedOrder.orderId}
          billNo={selectedOrder.billNo}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
};

export default ReturnSearch;
